import { useEffect, useState } from 'react'
import { supabase } from '@/shared/lib/supabase'
import { API_URL } from '@/shared/lib/constants'

type ServiceStatus = 'online' | 'offline' | 'checking' | 'unknown'

interface HealthState {
  backend: ServiceStatus
  supabase: ServiceStatus
  lastCheck: Date | null
}

const statusConfig: Record<ServiceStatus, { dot: string; label: string; pulse: string }> = {
  online: {
    dot: 'bg-green-500',
    label: 'text-green-700 dark:text-green-400',
    pulse: 'animate-pulse',
  },
  offline: {
    dot: 'bg-red-500',
    label: 'text-red-700 dark:text-red-400',
    pulse: '',
  },
  unknown: {
    dot: 'bg-gray-400 dark:bg-legal-500',
    label: 'text-gray-500 dark:text-legal-400',
    pulse: '',
  },
  checking: {
    dot: 'bg-yellow-500',
    label: 'text-yellow-700 dark:text-yellow-400',
    pulse: 'animate-pulse',
  },
}

const STATUS_LABEL: Record<ServiceStatus, string> = {
  online: 'OK',
  offline: 'Caído',
  unknown: '—',
  checking: 'Chequeando',
}

const CHECK_INTERVAL_MS = 60_000 // 1 min

async function checkBackend(): Promise<ServiceStatus> {
  try {
    const url = API_URL ? `${API_URL}/api/health` : '/api/health'
    const res = await fetch(url, { method: 'GET', signal: AbortSignal.timeout(5000) })
    return res.ok ? 'online' : 'offline'
  } catch {
    return 'offline'
  }
}

async function checkSupabase(): Promise<ServiceStatus> {
  if (!supabase) return 'unknown'
  try {
    const { error } = await supabase.from('sgw_signatures').select('id', { count: 'exact', head: true }).limit(1)
    return error ? 'offline' : 'online'
  } catch {
    return 'offline'
  }
}

function StatusDot({ status }: { status: ServiceStatus }) {
  const cfg = statusConfig[status]
  return <span className={`inline-block w-2 h-2 rounded-full ${cfg.dot} ${cfg.pulse}`} />
}

/**
 * Status bar fija al fondo de pantalla. Patrón inspirado en Maskedata:
 * - Izquierda: copyright + Arteclaw
 * - Derecha: dots de estado de servicios (Backend FastAPI, Supabase)
 *
 * Health check cada 1 min. NO se renderiza en la landing pública (allá
 * va Footer en su lugar).
 */
export function StatusBar() {
  const [health, setHealth] = useState<HealthState>({
    backend: 'checking',
    supabase: 'checking',
    lastCheck: null,
  })

  useEffect(() => {
    let cancelled = false

    const runChecks = async () => {
      const [backend, supa] = await Promise.all([checkBackend(), checkSupabase()])
      if (cancelled) return
      setHealth({ backend, supabase: supa, lastCheck: new Date() })
    }

    runChecks()
    const interval = setInterval(runChecks, CHECK_INTERVAL_MS)
    return () => {
      cancelled = true
      clearInterval(interval)
    }
  }, [])

  const backendCfg = statusConfig[health.backend]
  const supaCfg = statusConfig[health.supabase]
  const year = new Date().getFullYear()

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-30 flex items-center bg-white dark:bg-legal-800 border-t border-legal-200 dark:border-legal-700 text-xs select-none"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      <div className="flex items-center w-full h-7">
        <div className="flex items-center gap-1.5 px-2 sm:px-3 h-full text-legal-400 dark:text-legal-500 min-w-0 flex-1 sm:flex-initial">
          <span className="truncate">
            <span className="hidden sm:inline">© {year} SignWiz · Desarrollado por </span>
            <span className="sm:hidden">© {year} </span>
            <a
              href="https://arteclaw.com"
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-primary-500/70 dark:text-primary-400/70 hover:text-primary-600 dark:hover:text-primary-300 transition-colors"
            >
              Arteclaw
            </a>
          </span>
        </div>

        <div className="hidden sm:block flex-1" />

        <div className="flex items-center gap-1.5 px-2 sm:px-3 h-full border-l border-legal-200 dark:border-legal-700 flex-shrink-0">
          <StatusDot status={health.backend} />
          <span className={`font-medium ${backendCfg.label}`}>
            API <span className="hidden sm:inline">{STATUS_LABEL[health.backend]}</span>
          </span>
        </div>

        <div className="flex items-center gap-1.5 px-2 sm:px-3 h-full border-l border-legal-200 dark:border-legal-700 flex-shrink-0">
          <StatusDot status={health.supabase} />
          <span className={`font-medium ${supaCfg.label}`}>
            DB <span className="hidden sm:inline">{STATUS_LABEL[health.supabase]}</span>
          </span>
        </div>
      </div>
    </div>
  )
}
